/**
 * All Framer Motion animation variants for the Eggs Fighting Game.
 * Defined as module-level constants — never inline inside components.
 */
import { GAME_CONFIG } from "../config/gameConfig";

const { animation } = GAME_CONFIG;
const fC = animation.fightClashes;
const s = animation.shake;
const totalMs = GAME_CONFIG.timing.fightDurationMs;

/** Convert milliseconds → fraction of total fight duration (for Framer Motion `times`) */
const ft = (ms: number): number => ms / totalMs;

// ─── Fight sequence keyframe arrays ───────────────────────────────────────────
//
// 11 keyframe points per egg:
//   0  → idle
//   1  → CLASH 1 — eggs meet at center
//   2  → recoil 1
//   3  → jitter forward
//   4  → jitter back
//   5  → CLASH 2
//   6  → recoil 2
//   7  → jitter forward
//   8  → jitter back
//   9  → FINAL CLASH
//   10 → hold at center
//
// X: eggs converge to arena center (same as original horizontal clash).
// Y: layered on top — attacker descends (+Y), defender rises (−Y) as they clash.
//    At each clash point the eggs are side-by-side at center X, vertically offset
//    so attacker is below center and defender is above — they do NOT overlap.

const fightKfTimes: number[] = [
  0,
  ft(fC.clash1Ms),
  ft(fC.clash1Ms + 120),
  ft(fC.clash1Ms + 260),
  ft(fC.clash1Ms + 410),
  ft(fC.clash2Ms),
  ft(fC.clash2Ms + 130),
  ft(fC.clash2Ms + 310),
  ft(fC.clash2Ms + 540),
  ft(fC.clash3Ms),
  1.0,
];

// X: both eggs travel to the exact arena center (eggFightCenterX) so they share
// the same vertical axis. Recoil distances are preserved from the original config.
const cX = animation.eggFightCenterX;
const recoilPull1 = animation.eggFightTranslateX - fC.recoil1X; // 28 px pullback
const recoilPull2 = animation.eggFightTranslateX - fC.recoil2X; // 38 px pullback
const r1 = cX - recoilPull1;
const r2 = cX - recoilPull2;

const leftFightX: number[] = [
  -animation.eggIdleOffsetX, // 0: idle
  cX,                        // 1: CLASH 1 — egg is at arena center
  r1,                        // 2: recoil 1
  r1 + 14,                   // 3: jitter forward
  r1 - 8,                    // 4: jitter back
  cX,                        // 5: CLASH 2
  r2,                        // 6: recoil 2
  r2 + 18,                   // 7: jitter forward
  r2 - 12,                   // 8: jitter back
  cX,                        // 9: FINAL CLASH
  cX,                        // 10: hold
];

const travelY = animation.eggFightTravelY;

// Recoil Y distances scaled proportionally to the X recoil ratios so the
// post-clash pullback feels the same as the original horizontal animation.
const rY1 = travelY * (fC.recoil1X / animation.eggFightTranslateX); // ≈ 83
const rY2 = travelY * (fC.recoil2X / animation.eggFightTranslateX); // ≈ 60

// Attacker: moves UP (−Y) so its downward-pointing tip (rotated 180°) descends
// toward y = 0 where it meets the defender's upward-pointing tip.
const fightY_attacker: number[] = [
  0,          //  0: idle
  -travelY,   //  1: CLASH 1 — attacker tip at y ≈ 0 (meets defender)
  -rY1,       //  2: recoil back down
  -rY1 + 14,  //  3: jitter up
  -rY1 - 8,   //  4: jitter down
  -travelY,   //  5: CLASH 2
  -rY2,       //  6: recoil back down
  -rY2 + 18,  //  7: jitter up
  -rY2 - 12,  //  8: jitter down
  -travelY,   //  9: FINAL CLASH
  -travelY,   // 10: hold
];

// Defender: moves DOWN (+Y) so its upward-pointing tip ascends toward y = 0.
const fightY_defender: number[] = fightY_attacker.map((y) => -y);

const fightScales: number[] = [
  animation.scales.idle,
  animation.scales.impact,
  animation.scales.fight,
  animation.scales.fight * 1.04,
  animation.scales.fight * 0.96,
  animation.scales.clash2,
  animation.scales.fight,
  animation.scales.fight * 1.05,
  animation.scales.fight * 0.95,
  animation.scales.clash3,
  animation.scales.clash3,
];

// Rotation — attacker: tip faces DOWN (≈180°) throughout fight.
const leftFightRotateAttacker: number[] = [
  -animation.eggIdleRotate, 180, 172, 183, 168, 180, 170, 183, 165, 180, 180,
];
const rightFightRotateAttacker: number[] = [
  animation.eggIdleRotate, 180, 172, 183, 168, 180, 170, 183, 165, 180, 180,
];

// Rotation — defender: tip stays facing UP (≈0°) throughout fight.
const leftFightRotateDefender: number[] = [
  -animation.eggIdleRotate, 0, -5, 2, -3, 0, -4, 3, -5, 0, 0,
];
const rightFightRotateDefender: number[] = [
  animation.eggIdleRotate, 0, -5, 2, -3, 0, -4, 3, -5, 0, 0,
];

const fightTransition = {
  duration: animation.durations.fightSequence,
  times: fightKfTimes,
  ease: "linear" as const,
};

// ─── Modal ────────────────────────────────────────────────────────────────────

/** Full-screen backdrop fade */
export const backdropVariants: any = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.35 } },
  exit: { opacity: 0, transition: { duration: 0.25 } },
};

/** Modal card entrance / exit */
export const modalVariants: any = {
  hidden: { opacity: 0, scale: 0.9, y: 24 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.45, ease: animation.easing.bounce },
  },
  exit: {
    opacity: 0,
    scale: 0.88,
    y: 32,
    transition: { duration: 0.28 },
  },
};

// ─── Screen Shake ─────────────────────────────────────────────────────────────

/** Applied to the arena container. Three escalating clash intensities + legacy. */
export const shakeVariants: any = {
  idle: { x: 0, y: 0 },

  /** Clash 1 — sharp but contained */
  shake1: {
    x: [0, s.clash1X, -s.clash1X, s.clash1X * 0.5, 0],
    y: [0, -s.clash1Y, s.clash1Y, -s.clash1Y * 0.4, 0],
    transition: {
      duration: s.clash1Duration,
      times: [0, 0.2, 0.5, 0.75, 1],
    },
  },

  /** Clash 2 — harder, longer */
  shake2: {
    x: [0, s.clash2X, -s.clash2X, s.clash2X * 0.65, -s.clash2X * 0.3, 0],
    y: [0, -s.clash2Y, s.clash2Y, -s.clash2Y * 0.5, s.clash2Y * 0.2, 0],
    transition: {
      duration: s.clash2Duration,
      times: [0, 0.18, 0.42, 0.65, 0.82, 1],
    },
  },

  /** Clash 3 / final — brutal, full body */
  shake3: {
    x: [
      0,
      s.clash3X,
      -s.clash3X,
      s.clash3X * 0.72,
      -s.clash3X * 0.5,
      s.clash3X * 0.28,
      -s.clash3X * 0.12,
      0,
    ],
    y: [
      0,
      -s.clash3Y,
      s.clash3Y,
      -s.clash3Y * 0.65,
      s.clash3Y * 0.4,
      -s.clash3Y * 0.2,
      s.clash3Y * 0.08,
      0,
    ],
    transition: {
      duration: s.clash3Duration,
      times: [0, 0.11, 0.26, 0.42, 0.58, 0.73, 0.87, 1],
    },
  },

  /** Legacy — used for the impact phase (same strength as clash3) */
  shake: {
    x: [
      0,
      s.intensityX,
      -s.intensityX,
      s.intensityX * 0.65,
      -s.intensityX * 0.65,
      s.intensityX * 0.3,
      0,
    ],
    y: [
      0,
      -s.intensityY,
      s.intensityY,
      -s.intensityY * 0.5,
      s.intensityY * 0.5,
      0,
      0,
    ],
    transition: {
      duration: s.duration,
      times: [0, 0.15, 0.3, 0.48, 0.65, 0.82, 1],
    },
  },
};

// ─── Eggs ─────────────────────────────────────────────────────────────────────

/** Left egg (enemy / defender side) */
export const leftEggVariants: any = {
  idle: {
    x: -animation.eggIdleOffsetX,
    y: 0,
    scale: animation.scales.idle,
    rotate: -animation.eggIdleRotate,
    opacity: 1,
    transition: { duration: animation.durations.eggReturn, ease: "easeOut" },
  },

  /** Attacker: converges to center X + descends (+Y). Tip faces down. */
  fightingAttacker: {
    x: leftFightX,
    y: fightY_attacker,
    scale: fightScales,
    rotate: leftFightRotateAttacker,
    opacity: 1,
    transition: fightTransition,
  },

  /** Defender: converges to center X + rises (−Y). Tip stays up. */
  fightingDefender: {
    x: leftFightX,
    y: fightY_defender,
    scale: fightScales,
    rotate: leftFightRotateDefender,
    opacity: 1,
    transition: fightTransition,
  },

  /** Winner holds at arena center after the fight sequence resolves */
  impact: {
    x: animation.eggFightCenterX,
    y: 0,
    scale: animation.scales.clash3,
    rotate: 5,
    opacity: 1,
    transition: { duration: 0.2, ease: "easeOut" },
  },

  won: {
    x: animation.eggFightCenterX * 0.25,
    y: 0,
    scale: animation.scales.fight,
    rotate: -10,
    opacity: 1,
    transition: {
      duration: animation.durations.eggReturn,
      ease: animation.easing.bounce,
    },
  },
  lost: {
    x: -animation.brokenEggFlybackX,
    y: 0,
    scale: animation.scales.broken,
    rotate: -animation.brokenEggRotation,
    opacity: animation.brokenEggOpacity,
    transition: {
      duration: animation.durations.eggBreak,
      ease: animation.easing.eggBreak,
    },
  },
};

/** Right egg (local player / attacker side) */
export const rightEggVariants: any = {
  idle: {
    x: animation.eggIdleOffsetX,
    y: 0,
    scale: animation.scales.idle,
    rotate: animation.eggIdleRotate,
    opacity: 1,
    transition: { duration: animation.durations.eggReturn, ease: "easeOut" },
  },

  /** Attacker: converges to center X + descends (+Y). Tip faces down. */
  fightingAttacker: {
    x: leftFightX.map((v) => -v),
    y: fightY_attacker,
    scale: fightScales,
    rotate: rightFightRotateAttacker,
    opacity: 1,
    transition: fightTransition,
  },

  /** Defender: converges to center X + rises (−Y). Tip stays up. */
  fightingDefender: {
    x: leftFightX.map((v) => -v),
    y: fightY_defender,
    scale: fightScales,
    rotate: rightFightRotateDefender,
    opacity: 1,
    transition: fightTransition,
  },

  /** Winner holds at arena center after the fight sequence resolves */
  impact: {
    x: -animation.eggFightCenterX,
    y: 0,
    scale: animation.scales.clash3,
    rotate: -5,
    opacity: 1,
    transition: { duration: 0.2, ease: "easeOut" },
  },

  won: {
    x: -(animation.eggFightCenterX * 0.25),
    y: 0,
    scale: animation.scales.fight,
    rotate: 10,
    opacity: 1,
    transition: {
      duration: animation.durations.eggReturn,
      ease: animation.easing.bounce,
    },
  },
  lost: {
    x: animation.brokenEggFlybackX,
    y: 0,
    scale: animation.scales.broken,
    rotate: animation.brokenEggRotation,
    opacity: animation.brokenEggOpacity,
    transition: {
      duration: animation.durations.eggBreak,
      ease: animation.easing.eggBreak,
    },
  },
};

// ─── Crack Overlay ────────────────────────────────────────────────────────────

export const crackVariants: any = {
  hidden: { opacity: 0, scale: 0.6 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.14, ease: "easeOut" },
  },
  exit: { opacity: 0, transition: { duration: 0.2 } },
};

// ─── Impact Star ──────────────────────────────────────────────────────────────

export const impactStarVariants: any = {
  hidden: {
    scale: 0,
    opacity: 0,
    rotate: 0,
  },
  visible: {
    scale: animation.impactStar.maxScale,
    opacity: 1,
    rotate: animation.impactStar.rotateOffset,
    transition: {
      duration: animation.durations.impactStar,
      ease: animation.easing.bounce,
    },
  },
  exit: {
    scale: 2.2,
    opacity: 0,
    rotate: animation.impactStar.rotateOffset * 2,
    transition: { duration: 0.22 },
  },
};

/** Secondary ring burst that appears around the star */
export const impactRingVariants: any = {
  hidden: { scale: 0.4, opacity: 0.8 },
  visible: {
    scale: 2.4,
    opacity: 0,
    transition: { duration: 0.55, ease: "easeOut" },
  },
  exit: { opacity: 0 },
};

/** Spark lines radiating from impact */
export const sparkVariants: any = {
  hidden: { scaleX: 0, opacity: 1 },
  visible: {
    scaleX: 1,
    opacity: 0,
    transition: { duration: 0.4, ease: "easeOut" },
  },
  exit: { opacity: 0 },
};

/** Debris chunk flying outward from impact */
export const debrisVariants: any = {
  hidden: { scale: 0, opacity: 1 },
  visible: {
    scale: 1,
    opacity: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
  exit: { opacity: 0 },
};

// ─── Countdown ────────────────────────────────────────────────────────────────

/** Each countdown number (3, 2, 1) scales in from large, then shrinks out */
export const countdownNumberVariants: any = {
  enter: { scale: 3, opacity: 0, y: -10 },
  visible: {
    scale: 1,
    opacity: 1,
    y: 0,
    transition: {
      duration: animation.durations.countdownPop,
      ease: animation.easing.bounce,
    },
  },
  exit: {
    scale: 0.2,
    opacity: 0,
    y: 10,
    transition: { duration: 0.18 },
  },
};

/** "FIGHT!" text — more explosive entrance than number countdown */
export const fightTextVariants: any = {
  enter: { scale: 3.5, opacity: 0, rotate: -6, y: -15 },
  visible: {
    scale: 1,
    opacity: 1,
    rotate: 0,
    y: 0,
    transition: {
      duration: 0.32,
      ease: animation.easing.bounce,
    },
  },
  exit: {
    scale: 1.8,
    opacity: 0,
    transition: { duration: 0.2 },
  },
};

// ─── Round Result ─────────────────────────────────────────────────────────────

export const roundResultVariants: any = {
  hidden: { opacity: 0, y: 50, scale: 0.8 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: animation.durations.resultSlideIn,
      ease: animation.easing.bounce,
    },
  },
  exit: {
    opacity: 0,
    y: -40,
    scale: 0.88,
    transition: { duration: 0.28 },
  },
};

// ─── Game Over ────────────────────────────────────────────────────────────────

export const gameOverContainerVariants: any = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.4,
      staggerChildren: 0.12,
    },
  },
  exit: { opacity: 0, transition: { duration: 0.25 } },
};

export const gameOverItemVariants: any = {
  hidden: { opacity: 0, y: 30, scale: 0.85 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: animation.durations.gameOverFadeIn,
      ease: animation.easing.bounce,
    },
  },
};

// ─── Win Badge ────────────────────────────────────────────────────────────────

/** Per-round win dot that pops in when earned */
export const winDotVariants: any = {
  hidden: { scale: 0, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 18,
    },
  },
};

// ─── Role Label ───────────────────────────────────────────────────────────────

export const roleLabelVariants: any = {
  hidden: { opacity: 0, x: -12 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4, ease: "easeOut", delay: 0.1 },
  },
};
